import { t } from '@lingui/core/macro'

import { OrderStatus } from '@/core/graphql/types'

export function getOrderStatusLabel(status: OrderStatus): string {
  switch (status) {
    case OrderStatus.Accepted:
      return t`Order accepted`
    case OrderStatus.Canceled:
      return t`Order cancelled`
    case OrderStatus.Confirmed:
      return t`Confirmed`
    case OrderStatus.ConsultationCreated:
      return t`Consultation created`
    case OrderStatus.InProgress:
      return t`In progress`
    case OrderStatus.NoShow:
      return t`No show`
    case OrderStatus.Pending:
      return t`Pending`
    case OrderStatus.Refused:
      return t`Refused`
    case OrderStatus.Rescheduled:
      return t`Rescheduled`
    case OrderStatus.ExpertiseCreated:
      return t`Expertise created`
    case OrderStatus.Completed:
      return t`Completed`
    case OrderStatus.Draft:
      return t`Draft`
    default:
      return status
  }
}

export function getMeetingPlaceDescription(meetingPlace: { description?: string | null; placeId?: string | null }): string {
  switch (meetingPlace.placeId) {
    case 'VISIO_CONFERENCE':
      return t`Video visit`
    case 'AT_HOME':
      return t`At home`
    case 'CENTER':
      return t`At clinic`
    case 'SECOND_OPINION':
      return t`Second Opinion`
    default:
      return meetingPlace.description || ''
  }
}

export function getBgColorByStatus(status: OrderStatus): string {
  switch (status) {
    case OrderStatus.Accepted:
      return 'bg-green-50'
    case OrderStatus.ConsultationCreated:
      return 'bg-blue-50'
    case OrderStatus.ExpertiseCreated:
      return 'bg-purple-50'
    case OrderStatus.Canceled:
    case OrderStatus.Refused:
      return 'bg-red-50'
    case OrderStatus.Pending:
      return 'bg-yellow-50'
    case OrderStatus.Draft:
      return 'bg-gray-50'
    default:
      return 'bg-black-50'
  }
}

export function getTextColorByStatus(status: OrderStatus): string {
  switch (status) {
    case OrderStatus.Accepted:
      return 'text-green-500'
    case OrderStatus.ConsultationCreated:
      return 'text-blue-500'
    case OrderStatus.ExpertiseCreated:
      return 'text-purple-500'
    case OrderStatus.Canceled:
    case OrderStatus.Refused:
      return 'text-red-500'
    case OrderStatus.Pending:
      return 'text-yellow-500'
    case OrderStatus.Draft:
      return 'text-gray-500'
    default:
      return 'text-black-500'
  }
}

/**
 * High-level grouping used to filter orders in list views. Every {@link OrderStatus}
 * maps to exactly one category:
 * - `active`     — the request is still in flight (draft, pending, accepted, …).
 * - `completed`  — the request was fulfilled (a consultation/expertise was created, or it finished).
 * - `cancelled`  — the request will not proceed (cancelled, refused, no-show).
 */
export type OrderCategory = 'active' | 'completed' | 'cancelled'

export function getOrderCategory(status: OrderStatus): OrderCategory {
  switch (status) {
    case OrderStatus.Canceled:
    case OrderStatus.Refused:
    case OrderStatus.NoShow:
      return 'cancelled'
    case OrderStatus.Completed:
    case OrderStatus.ConsultationCreated:
    case OrderStatus.ExpertiseCreated:
      return 'completed'
    case OrderStatus.Draft:
    case OrderStatus.Pending:
    case OrderStatus.Accepted:
    case OrderStatus.Confirmed:
    case OrderStatus.InProgress:
    case OrderStatus.Rescheduled:
    default:
      return 'active'
  }
}
